// src/components/open-houses/checkin/CheckinCenterModal.jsx
// One home for everything check-in on an open house: launch the iPad kiosk,
// watch live traffic (+1 buttons for walk-ins), and afterwards sort each
// sign-in onto the Buyers or Listings pipeline (optionally saving the guest
// as a contact). Phone-friendly by design.
import { useEffect, useMemo, useState } from "react";
import { X, Minus } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useStateValue } from "../../../context/StateProvider";
import { deriveTraffic } from "../../../utils/checkinCounts";
import { isClosedNoFeedback } from "../../../utils/openHouseHelpers";
import {
  suggestedSort, checkinDisplayName, buildContactDoc, contactOwnerId,
  buildBuyerLead, buildListingLead, findOpenHouseSource, mmddyyyy,
} from "../../../utils/checkinSorting";
import {
  listenOpenHouseCheckins, bumpOpenHouseTraffic, markCheckinSorted,
  saveBuyerLead, saveListingLead, saveLeadSource, addContactToFirebase,
  subscribeOpenHouses,
} from "../../../utils/firebasefunctions";

export default function CheckinCenterModal({ open, openHouse, onClose }) {
  const [{ user, leadsource }] = useStateValue();
  const navigate = useNavigate();
  const [checkins, setCheckins] = useState([]);
  const [liveOh, setLiveOh] = useState(openHouse);
  const [choices, setChoices] = useState({}); // checkinId -> { sortAs, saveContact }
  const [sending, setSending] = useState(false);

  useEffect(() => { setLiveOh(openHouse); }, [openHouse]);

  // Live counters: reuse the page-wide openHouses subscription shape.
  useEffect(() => {
    if (!open || !openHouse?.id) return;
    const unsub = subscribeOpenHouses((rows) => {
      const hit = rows.find((r) => r.id === openHouse.id);
      if (hit) setLiveOh(hit);
    });
    return () => unsub && unsub();
  }, [open, openHouse?.id]);

  useEffect(() => {
    if (!open || !openHouse?.id) return;
    const unsub = listenOpenHouseCheckins(openHouse.id, setCheckins);
    return () => unsub && unsub();
  }, [open, openHouse?.id]);

  // Default a choice row per unsorted check-in from its intent.
  useEffect(() => {
    setChoices((prev) => {
      const nextChoices = { ...prev };
      checkins.forEach((c) => {
        if (!c.sortedAs && !nextChoices[c.id]) {
          nextChoices[c.id] = { sortAs: suggestedSort(c.intent), saveContact: false };
        }
      });
      return nextChoices;
    });
  }, [checkins]);

  const traffic = useMemo(() => deriveTraffic(liveOh), [liveOh]);
  const unsorted = checkins.filter((c) => !c.sortedAs);
  const sorted = checkins.filter((c) => !!c.sortedAs);
  const sendCount = unsorted.filter((c) => {
    const s = choices[c.id]?.sortAs;
    return s === "buyer" || s === "seller";
  }).length;

  if (!open || !openHouse) return null;

  const bump = async (patch) => {
    const r = await bumpOpenHouseTraffic(openHouse.id, patch);
    if (!r.success) toast.error("Could not update the count. Try again.");
  };

  const setChoice = (id, patch) =>
    setChoices((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  // Resolve (or create) the "Open House" system lead source once per send.
  const resolveSourceId = async () => {
    const existing = findOpenHouseSource(leadsource);
    if (existing) return existing;
    const r = await saveLeadSource({ name: "Open House", abbreviation: "OH", leadCommission: 0 });
    if (r.success) return r.id;
    toast.error("Could not create the Open House lead source.");
    return "";
  };

  const send = async () => {
    setSending(true);
    const sourceId = await resolveSourceId();
    if (!sourceId) { setSending(false); return; }
    let ok = 0, fail = 0;
    for (let i = 0; i < unsorted.length; i++) {
      const c = unsorted[i];
      const choice = choices[c.id] || {};
      try {
        if (choice.sortAs === "skipped") {
          const r = await markCheckinSorted(c.id, { sortedAs: "skipped", sortedBy: user.id });
          if (!r.success) throw new Error(r.error);
          continue;
        }
        if (choice.sortAs !== "buyer" && choice.sortAs !== "seller") continue;

        let contactId = c.contactId || "";
        if (choice.saveContact && !contactId) {
          contactId = `oh_${Date.now()}_${i}`;
          // Contact goes to the hosting agent's book, not the sorter's —
          // mirrors how the lead below is assigned.
          await addContactToFirebase(
            buildContactDoc(c, { id: contactId, userId: contactOwnerId(liveOh, user.id) }),
          );
        }
        const opts = {
          sourceId, contactId,
          // The questions exactly as this guest was asked (snapshot stored
          // by the kiosk), so notes keep readable prompts even if the
          // template changed since.
          template: { questions: c.askedQuestions || [] },
          fallbackAgent: { id: user.id, name: [user.firstname, user.lastname].filter(Boolean).join(" ") },
        };
        const r = choice.sortAs === "buyer"
          ? await saveBuyerLead(buildBuyerLead(c, liveOh, opts))
          : await saveListingLead(buildListingLead(c, liveOh, opts));
        if (!r.success) throw new Error(r.error);
        const m = await markCheckinSorted(c.id, {
          sortedAs: choice.sortAs, sortedBy: user.id, leadId: r.id, contactId,
        });
        if (!m.success) throw new Error(m.error);
        ok += 1;
      } catch (err) {
        console.error("Sort & Send failed for check-in", c.id, err);
        fail += 1;
      }
    }
    setSending(false);
    if (ok) toast.success(`${ok} lead${ok === 1 ? "" : "s"} sent to the pipelines.`);
    if (fail) toast.error(`${fail} could not be filed. They are still listed; try again.`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {openHouse.propertyAddress?.split(",")[0] || "Open House"}
            </h2>
            <p className="text-xs text-slate-500">{mmddyyyy(openHouse.date)} · {openHouse.hostingAgentName}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          {/* Closed means closed: no restarting the kiosk, but sorting the
              day's sign-ins below stays available. */}
          {!isClosedNoFeedback(liveOh || openHouse) && (
            <button
              onClick={() => navigate(`/dashboard/oh-checkin/${openHouse.id}`)}
              className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold rounded-lg py-3 mb-5"
            >
              Start Open House on this iPad
            </button>
          )}

          <div className="grid grid-cols-3 gap-2 mb-3">
            <Stat label="People" value={traffic.people} />
            <Stat label="Groups" value={traffic.groups} />
            <Stat label="Signed in" value={traffic.signedIn} />
          </div>
          <div className="flex gap-2 mb-1">
            <TapButton onClick={() => bump({ people: 1 })}>+1 Person</TapButton>
            <TapButton ghost onClick={() => bump({ groups: 1 })}>+1 Group</TapButton>
            <button
              onClick={() => bump({ people: -1 })}
              aria-label="Undo person"
              className="border border-slate-300 rounded-lg px-3 text-slate-500 hover:bg-slate-50"
            >
              <Minus size={16} />
            </button>
            <button
              onClick={() => bump({ groups: -1 })}
              aria-label="Undo group"
              className="border border-slate-300 rounded-lg px-3 text-slate-500 hover:bg-slate-50"
            >
              <Minus size={16} />
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mb-5">
            iPad sign-ins count automatically. Tap for walk-ins who skipped the iPad.
            The − buttons undo a person / a group.
          </p>

          <h3 className="text-sm font-bold text-slate-900 mb-2">
            Sort sign-ins {unsorted.length ? `(${unsorted.length} waiting)` : ""}
          </h3>
          {unsorted.length === 0 && (
            <p className="text-sm text-slate-500 mb-3">
              {checkins.length === 0 ? "No sign-ins yet." : "All sign-ins are sorted. Nice."}
            </p>
          )}
          <div className="space-y-3">
            {unsorted.map((c) => (
              <GuestRow
                key={c.id}
                checkin={c}
                choice={choices[c.id] || { sortAs: "skipped", saveContact: false }}
                onChange={(patch) => setChoice(c.id, patch)}
              />
            ))}
          </div>

          {unsorted.length > 0 && (
            <button
              onClick={send}
              disabled={sending}
              className="w-full mt-4 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold rounded-lg py-3"
            >
              {sending ? "Sending..." : sendCount > 0
                ? `Send ${sendCount} lead${sendCount === 1 ? "" : "s"} to pipelines`
                : "Apply sorting"}
            </button>
          )}

          {sorted.length > 0 && (
            <div className="mt-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Already sorted</h4>
              {sorted.map((c) => (
                <div key={c.id} className="text-sm text-slate-500 py-1">
                  {checkinDisplayName(c)} · {c.sortedAs === "skipped" ? "skipped" : `${c.sortedAs} lead`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg text-center py-2">
      <div className="text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

function TapButton({ children, onClick, ghost }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg py-3 font-bold ${
        ghost
          ? "border-2 border-slate-800 text-slate-800 hover:bg-slate-50"
          : "bg-slate-800 hover:bg-slate-900 text-white"
      }`}
    >
      {children}
    </button>
  );
}

const INTENT_BADGE = {
  buy: ["wants to buy", "text-blue-700"],
  sell: ["home to sell", "text-amber-700"],
  both: ["buy + sell", "text-emerald-700"],
  browsing: ["just browsing", "text-slate-400"],
};

function GuestRow({ checkin, choice, onChange }) {
  const [label, color] = INTENT_BADGE[checkin.intent] || ["", "text-slate-400"];
  const meta = [checkin.phone, checkin.email, checkin.partySize > 1 ? `group of ${checkin.partySize}` : ""]
    .filter(Boolean).join(" · ");
  const agentLine = checkin.hasAgent
    ? ["has agent:", checkin.agentName, checkin.agentBrokerage ? `(${checkin.agentBrokerage})` : ""]
        .filter(Boolean).join(" ")
    : "";
  const Pill = ({ value, children }) => (
    <button
      onClick={() => onChange({ sortAs: value })}
      className={`text-xs font-semibold rounded-full px-3 py-1.5 mr-1.5 border ${
        choice.sortAs === value
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
      }`}
    >
      {children}
    </button>
  );
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="font-semibold text-slate-900 text-sm">
        {checkinDisplayName(checkin) || "Unnamed guest"}{" "}
        {label && <span className={`font-normal text-xs ${color}`}>· {label}</span>}
      </div>
      {meta && <div className="text-xs text-slate-500 mt-0.5">{meta}</div>}
      {agentLine && <div className="text-xs text-slate-500">{agentLine}</div>}
      {(checkin.askedQuestions || [])
        .filter((q) => q && q.id && checkin.answers?.[q.id] != null && checkin.answers[q.id] !== "")
        .map((q) => (
          <div key={q.id} className="text-xs text-slate-500">
            {q.prompt}: {String(checkin.answers[q.id])}
          </div>
        ))}
      <div className="mt-2">
        <Pill value="buyer">Buyer lead</Pill>
        <Pill value="seller">Seller lead</Pill>
        <Pill value="skipped">Skip</Pill>
      </div>
      {(choice.sortAs === "buyer" || choice.sortAs === "seller") && (
        <label className="flex items-center gap-2 text-xs text-slate-600 mt-2">
          <input
            type="checkbox"
            checked={!!choice.saveContact}
            onChange={(e) => onChange({ saveContact: e.target.checked })}
          />
          Also save to Contacts
        </label>
      )}
    </div>
  );
}
